import {AspectRatio, Box, Stack} from "@mui/joy";
import {MAX_THUMBNAIL_DIM, photoUrl, PhotoZoomContext, thumbnailUrl} from "../photo.tsx";
import {PropsWithChildren, useContext} from "react";
import {FontAwesomeIcon, FontAwesomeIconProps} from "@fortawesome/react-fontawesome";
import {faComment} from "@fortawesome/pro-regular-svg-icons";
import {AgentCapsule} from "../agent/capsule.tsx";
import {z} from "zod/v4";
import {Flexidate, TstzSpan} from "../tstzspan.ts";
import {BaseTimelineScene} from "../timeline/timeline.tsx";
import {SceneTileContent} from "../dispatch/DispatchTile.tsx";
import {StampFlavorTypeRecord, StampSubject} from "./stampFlavorTypeRecord.tsx";
import {StampFlavorIconMap} from "./stampFlavorIconMap.tsx";
import {SceneViewProps} from "../timeline/sceneViewProps.tsx";
import {DiarySceneView} from "../timeline/diary.tsx";
import {AgentStub, Photo} from "../rpc/webrpc-client.gen.ts";
import i18next from "i18next";

// export type StampSubjectFlavor<SubjectT extends StampSubject> = `${SubjectT}Stamp`;
// export type OnlyStampSubject<WithStampT extends string> = WithStampT extends `${infer Prefix}Stamp` ? Prefix : never;

// export const <SubjT extends StampSubject>(subj: SubjT) {
//     switch (subj) {
//         case "waste": return WasteStampFlavorSchema;
//         case "bundle": return BundleStampFlavorSchema;
//         case "job": return JobStampFlavorSchema;
//         case "agent": return AgentStampFlavorSchema;
//         case "truck": return TruckStampFlavorSchema;
//     }
// }

// function subjectFlavorToSubject<SubjectT extends StampSubject>(subjectFlavor: StampSubjectFlavor<SubjectT>): SubjectT {
//     const sfx = "Flavor";
//     if (!subjectFlavor.endsWith(sfx)) {
//         throw new Error(`${subjectFlavor} not a proper subject flavor`);
//     }
//     const trimmed = subjectFlavor.slice(0, -sfx.length);
//     return trimmed as SubjectT;
// }

export const FlavorlessStamp = z.object({
    id: z.optional(z.string()),
    flavor: z.optional(z.never()),
    posted: Flexidate,
    author: AgentStub,
    photo: z.optional(Photo),
    caption: z.optional(z.string()),
    validSpan: z.optional(z.union([z.string(), TstzSpan])),
});
export type FlavorlessStamp = z.infer<typeof FlavorlessStamp>;
export type FlavoredStamp<FlavorT> = Omit<FlavorlessStamp, "flavor"> & { flavor?: FlavorT };

export interface StampScene<SubjectT extends StampSubject> extends BaseTimelineScene {
    subjectFlavor: SubjectT;
    stamp: FlavoredStamp<StampFlavorTypeRecord[SubjectT]> | FlavorlessStamp;
}

// export type FlavoredStampScene<FlavorT> =

// const stampSceneTypeRecord = {
//     waste: {stampFlavor: WasteStampFlavor, scene: WasteStampScene},
// };

interface LabeledIcon {
    icon?: FontAwesomeIconProps;
    label?: string;
}

function labeledIconForScene<const SubjT extends StampSubject>({scene, iconMap: iconsByFlavor}: {
    scene: StampScene<SubjT>,
    iconMap: StampFlavorIconMap<SubjT>
}): LabeledIcon | undefined {
    const subj = scene.subjectFlavor;
    const stampFlavor = scene.stamp.flavor;
    if (!stampFlavor) {
        return undefined;
    }
    // const iconsByFlavor = iconMapsForStampSubject(subj);
    const labelTk = `stampFlavorTileLabels.${subj}.${stampFlavor}`;
    // @ts-expect-error type cannot properly be inferred because of the interpolation
    const label = i18next.t(labelTk) as string | undefined;
    const icon = iconsByFlavor[stampFlavor];
    const rv = {icon, label};
    return rv;
}

// function stampFlavorIconFromScene<const SubjT extends StampSubject>(scene: StampScene<SubjT>) {
//     const subj = scene.subjectFlavor;
//     const iconsByFlavor = iconMapsByStampFlavor[subj];
//     const stampFlavor = scene.stamp.flavor ?? "";
//     return iconsByFlavor[stampFlavor];
// }
//
// function stampFlavorLabelFromScene<const SubjT extends StampSubject>(scene: StampScene<SubjT>) {
//     const subj = scene.subjectFlavor;
//     const stampFlavor = scene.stamp.flavor;
//     const tk = `stampFlavorTileLabels.${subj}.${stampFlavor}`;
//     // @ts-expect-error type cannot properly be inferred because of the interpolation
//     return <Box>{i18next.t(tk)}</Box>;
// }

// export const Stamp = z.union([
//     WasteStamp,
//     BundleStamp,
//     JobStamp,
//     AgentStamp,
//     TruckStamp,
// ]);
//
// export type Stamp = z.infer<typeof Stamp>;
//
// export interface GenericStamp<T extends StampFlavor> {
//     id?: string;
//     posted: string;
//     author: AgentStub;
//     flavor?: T | null;
//     photo?: Photo;
//     caption?: string;
//     validSpan?: string;
//     canCancel?: boolean;
// }

// export interface StampTimelineCellContentProps<
//     FlavorT extends StampFlavor,
//     StampT extends GenericStamp<FlavorT>> extends SheetProps {
//     stamp: StampT;
//     extraChildren?: ReactNode;
//     performCancel?: (stamp: StampT) => Promise<unknown>;
// }


// function TruckStampTimelineCellContent({scene}: { scene: TruckStampScene }) {
//     const icon = iconForTruckStampFlavor[scene.stampFlavor];
//     return <Stack direction="row" sx={{alignItems: "center"}} spacing={0.6}>
//         {icon ? <FontAwesomeIcon {...icon} size="2x"/> : null}
//         <Box>{t(`truckStampDescription.${scene.stampFlavor}`)}</Box>
//     </Stack>;
// }
//

export function StampTimelineCellContent<SubjName extends StampSubject>({scene, iconMap, children}: PropsWithChildren<{
    scene: StampScene<SubjName>,
    iconMap: StampFlavorIconMap<SubjName>,
}>) {
    const {setUrl: setZoomUrl} = useContext(PhotoZoomContext);
    const stamp = scene.stamp;
    const labeledIcon = labeledIconForScene({scene, iconMap});
    const icon = labeledIcon?.icon;
    const label = labeledIcon?.label;

    return <Stack direction="column" sx={{py: 0.6, alignItems: "center", justifyContent: "center"}}>
        {/*{performCancel*/}
        {/*    ? <IconButton color="danger" variant="soft"*/}
        {/*                  sx={{*/}
        {/*                      position: "absolute",*/}
        {/*                      top: 12,*/}
        {/*                      right: 12,*/}
        {/*                      p: 1.2,*/}
        {/*                  }}>*/}
        {/*        <FontAwesomeIcon icon={faTrash} size="2x"/>*/}
        {/*    </IconButton>*/}
        {/*    : null}*/}
        <Stack direction="row" spacing={1.2} sx={{px: 1, width: "100%"}}>
            {stamp.photo
                ? <Stack direction="column" sx={{
                    alignItems: "center", justifyContent: "center",
                }}>
                    <Stack sx={{width: MAX_THUMBNAIL_DIM, height: MAX_THUMBNAIL_DIM, justifyContent: "center"}}>
                        {/*<AspectRatio objectFit="contain" color={color} variant={variant}>*/}
                        <AspectRatio objectFit="contain">
                            <img src={thumbnailUrl(stamp.photo.slug)}
                                 onClick={() => {
                                     if (stamp.photo?.slug) {
                                         setZoomUrl(photoUrl(stamp.photo.slug));
                                     }
                                 }}/>
                        </AspectRatio>
                    </Stack>
                </Stack>
                : null}
            <Stack direction="column" spacing={0.8} sx={{flex: 1}}>
                {stamp.caption
                    ? <Stack direction="row" sx={{py: 1.6, alignItems: "center"}}>
                        <FontAwesomeIcon icon={faComment}/>
                        <Box sx={{pl: 0.8}}>{stamp.caption}</Box>
                    </Stack>
                    : null}
                <AgentCapsule agent={stamp.author} sx={{fontSize: "md"}}/>
                <Stack direction="row">
                    {icon ? <FontAwesomeIcon {...icon} size="2x"/> : null}
                    {label ? <Box>{label}</Box> : null}
                    {children}
                </Stack>
            </Stack>
        </Stack>
    </Stack>
}

export function StampSceneView<SubjT extends StampSubject>(
    {
        scene,
        color,
        variant,
        iconMap,
        ...rest
    }: SceneViewProps<StampScene<SubjT>> & {
        iconMap: StampFlavorIconMap<SubjT>
    }) {
    return <DiarySceneView scene={scene} color={color} variant={variant} {...rest}>
        <StampTimelineCellContent<SubjT> scene={scene} iconMap={iconMap}/>
    </DiarySceneView>
}

export function StampTimelineTileContent<SubjT extends StampSubject>(
    props: SceneViewProps<StampScene<SubjT>> & {
        iconMap: StampFlavorIconMap<SubjT>
    }) {
    const {scene, color, variant, iconMap, children} = props;
    const labeledIcon = labeledIconForScene({scene, iconMap});
    const icon = labeledIcon?.icon;
    const what = labeledIcon?.label ? <Box>{labeledIcon.label}</Box> : undefined;

    return <SceneTileContent color={color} variant={variant} icon={icon}
                             what={what} when={scene.when}>{children}</SceneTileContent>

}

//
// export interface StampTimelineProps<
//     FlavorT extends StampFlavor,
//     StampT extends GenericStamp<FlavorT>,
//     SpawnFoodT extends GenericFlavorlessStampSpawnFood,
// > {
//     targetId: string;
//     targetLabel: string;
//     stampVerb: string;
//     stamps: StampT[];
//     makeFlavorlessSpawnFood: (targetId: string, genericFood: GenericFlavorlessStampSpawnFood) => SpawnFoodT;
//     performSpawn: (food: SpawnFoodT) => Promise<unknown>;
//     invalidateAfterSpawn: () => Promise<unknown>;
//     renderFlavor: (stamp: StampT) => React.JSX.Element;
//     performCancel?: (stamp: StampT) => Promise<unknown>;
//     invalidateAfterCancel?: () => Promise<unknown>;
//     flavoredActionButtons?: React.JSX.Element[];
// }
//
// export function StampTimeline<
//     FlavorT extends StampFlavor,
//     StampT extends GenericStamp<FlavorT>,
//     SpawnFoodT extends GenericFlavorlessStampSpawnFood,
// >({
//       targetId,
//       targetLabel,
//       stampVerb,
//       stamps,
//       makeFlavorlessSpawnFood,
//       performSpawn,
//       invalidateAfterSpawn,
//       renderFlavor,
//       flavoredActionButtons,
//       performCancel,
//       invalidateAfterCancel,
//   }: StampTimelineProps<FlavorT, StampT, SpawnFoodT>) {
//     const {t} = useTranslation();
//     const prevStamps = usePrevious(stamps);
//     const numCurrStamps = stamps.length;
//     // on first run, usePrevious returns null or undefined. we have to
//     // guard against that
//     // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
//     const numPrevStamps = prevStamps?.length ?? 0;
//     const hasGrown = numPrevStamps > 0 && numCurrStamps > numPrevStamps;
//
//     const {
//         control,
//         handleSubmit,
//         reset: resetForm,
//     } = useForm<NewStampForm>({
//         defaultValues: {
//             photo: null,
//             caption: "",
//         }
//     });
//     const [isUploadingPhoto, setIsUploadingPhoto] = useState<boolean>(false);
//     const [photoUploadProgress, setPhotoUploadProgress] = useState<number | null>(null);
//
//     // const upscrollRef = useRef<HTMLDivElement>(null);
//     // const downscrollRef = useRef<HTMLDivElement>(null);
//
//     // eslint-disable-next-line @typescript-eslint/unbound-method
//     const {scrollToTop, scrollToBottom} = useContext(ScrollableViewContext);
//
//     useEffect(() => {
//         if (hasGrown) {
//             setTimeout(() => {
//                 scrollToBottom();
//             }, 0);
//         }
//     }, [hasGrown, scrollToBottom]);
//
//
//     return <Sheet>
//         <IconButton sx={{position: "fixed", right: 24, top: 64, mb: 2, px: 1}}
//                     color="primary" variant="soft"
//                     onClick={() => {
//                         scrollToBottom();
//                     }}>
//             <FontAwesomeIcon icon={faChevronsDown} size="2x"/>
//         </IconButton>
//         <IconButton sx={{position: "fixed", right: 24, bottom: 4, px: 1, mt: 2}}
//                     color="primary" variant="soft"
//                     onClick={() => {
//                         scrollToTop();
//                     }}>
//             <FontAwesomeIcon icon={faChevronsUp} size="2x"/>
//         </IconButton>
//         <Stack direction="row" sx={{
//             justifyContent: "center",
//             position: "sticky", top: 0, zIndex: 120,
//             bgcolor: "primary.100", width: "80%", mx: "auto"
//         }}>
//             <Typography level="h3" sx={{color: "primary.900"}}>{targetLabel}</Typography>
//         </Stack>
//         <Stack direction="column" spacing={2} sx={{alignItems: "center"}}>
//             <List sx={{flex: 1}}>
//                 {stamps.map(stamp =>
//                     <ListItem key={stamp.id}>
//                         <StampTimelineEntry stamp={stamp} renderFlavor={renderFlavor}/>
//                     </ListItem>
//                 )}
//             </List>
//         </Stack>
//         <Box sx={{
//             position: "sticky", width: "90%",
//             borderColor: "primary.900", borderWidth: 3, borderStyle: "groove",
//             backgroundColor: "white", display: "flex",
//             justifyContent: "center", alignItems: "center",
//             bottom: 6, left: 6,
//         }}>
//             <Stack direction="column" spacing={1} sx={{p: 0.4}}>
//                 {flavoredActionButtons
//                     ? <Stack direction="row" spacing={2} sx={{
//                         width: "100%", alignItems: "center", justifyContent: "space-evenly"
//                     }}>
//                         {...[flavoredActionButtons]}
//                     </Stack>
//                     : null}
//                 {isUploadingPhoto
//                     ? <LinearProgress sx={{width: "95%"}}
//                                       color="primary" size="lg" thickness={16}
//                                       determinate={!!photoUploadProgress}
//                                       value={photoUploadProgress ?? undefined}
//                     />
//                     : null}
//                 <form onSubmit={onSubmit}>
//                     <Stack direction="row" sx={{
//                         alignItems: "center",
//                         justifyContent: "space-between",
//                         m: 0.8,
//                     }} spacing={3}>
//                         <Controller control={control} name="photo"
//                                     render={({field}) =>
//                                         <PhotoInput value={field.value}
//                                                     onChange={field.onChange}/>}/>
//                         <Controller control={control} name="caption"
//                                     render={({field}) =>
//                                         <FormControl sx={{flex: 1}}>
//                                             <FormLabel>{t("stampComment")}</FormLabel>
//                                             <Textarea minRows={2}
//                                                       value={field.value}
//                                                       onChange={field.onChange}/>
//                                         </FormControl>
//                                     }/>
//                         <Button type="submit" size="lg" sx={{px: 4, py: 2}}
//                         >{t("add")}</Button>
//                     </Stack>
//                 </form>
//             </Stack>
//         </Box>
//     </Sheet>
// }

